<template>
    <div class="vendor-comp-item-list">
        <div
            v-if="!firstLoadComplete"
            key="loadingState"
            class="border bg-gray-100 rounded-lg d-flex align-items-center justify-content-center p-3 min-height-200 text-muted"
        >
            <div class="text-center">
                <b-spinner variant="secondary"></b-spinner>
                <div class="special-text mt-3">Loading...</div>
            </div>
        </div>
        <div v-else key="docList" class="row flex-row-reverse">
            <div class="col-12 col-md-4 col-lg-3">
                <div v-if="allowCreate" class="w-100">
                    <div class="w-100">
                        <b-button 
                            v-if="allowCreate" 
                            variant="success" 
                            block 
                            :disabled="!createButtonEnabled" 
                            @click="showAddModal"
                        >+ Add Document</b-button>
                    </div>
                </div>
                <hr v-if="showFilterHr" class="my-3" />
                <!-- FILTERS -->
                <div v-if="anyFiltersEnabled" class="special-text mb-3">Filters</div>
                <div class="d-flex flex-column gap-3">
                    <div v-if="enableVendorFilter" class="vci-list-filter-wrapper">
                        <div class="vci-list-filter-heading">
                            <h6 class="mb-0">Vendors</h6>
                            <div class="text-muted" style="font-size: 0.9em;">{{ (selectedVendorIds.length ? `${selectedVendorIds.length} of ` : '') + vendorFilterOptions.length }} Vendors</div>
                        </div>
                        <div class="px-3 pt-2 d-flex gap-4 align-items-center justify-content-start bg-white">
                            <b-link variant="bare-primary" class="font-size-70 text-decoration-none" @click="() => selectedVendorIds = vendorFilterOptions.map(o => o.id)">Select All</b-link>
                            <b-link variant="bare-primary" class="font-size-70 text-decoration-none" @click="() => selectedVendorIds = []">Reset</b-link>
                        </div>
                        <PrimeListbox 
                            v-model="selectedVendorIds" 
                            :options="vendorFilterOptions" 
                            dataKey="id"
                            optionValue="id"
                            optionLabel="name" 
                            :multiple="true"
                            listStyle="max-height:500px"
                            :filter="vendorFilterOptions.length > 10"
                            filterPlaceholder="Vendor Name..."
                            placeholder="Select Vendors"
                        >
                            <template #option="slotProps">
                                <div class="d-flex align-items-center gap-2 font-size-90 line-height-100">
                                    <b-icon :icon="selectedVendorIds.includes(slotProps.option.id) ? 'check-square-fill' : 'square'" variant="primary"></b-icon>
                                    <div>{{ slotProps.option.name }}</div>
                                </div>
                            </template>
                        </PrimeListbox>
                    </div>
                    <div v-if="enableKindFilter" class="vci-list-filter-wrapper">
                        <div class="vci-list-filter-heading">
                            <h6 class="mb-0">Kinds of Documents</h6>
                            <div class="text-muted" style="font-size: 0.9em;">{{ (selectedKindIds.length ? `${selectedKindIds.length} of ` : '') + kindFilterOptions.length }} Kinds</div>
                        </div>
                        <div class="px-3 pt-2 d-flex gap-4 align-items-center justify-content-start bg-white">
                            <b-link variant="bare-primary" class="font-size-70 text-decoration-none" @click="() => selectedKindIds = kindFilterOptions.map(o => o.id)">Select All</b-link>
                            <b-link variant="bare-primary" class="font-size-70 text-decoration-none" @click="() => selectedKindIds = []">Reset</b-link>
                        </div>
                        <PrimeListbox 
                            v-model="selectedKindIds" 
                            :options="kindFilterOptions" 
                            dataKey="id"
                            optionValue="id"
                            optionLabel="label" 
                            :multiple="true"
                            listStyle="max-height:400px"
                            :filter="kindFilterOptions.length > 10"
                            filterPlaceholder="Kinds of Documents..."
                            placeholder="Select Kinds of Documents"
                        >
                            <template #option="slotProps">
                                <div class="d-flex align-items-center gap-2 font-size-90 line-height-100">
                                    <b-icon :icon="selectedKindIds.includes(slotProps.option.id) ? 'check-square-fill' : 'square'" variant="primary"></b-icon>
                                    <!-- <div>{{ slotProps.option.label }}</div> -->
                                    <CompKindChip :kind="slotProps.option"></CompKindChip>
                                </div>
                            </template>
                        </PrimeListbox>
                    </div>
                    <div v-if="enableCompItemFilter" class="vci-list-filter-wrapper">
                        <div class="vci-list-filter-heading">
                            <h6 class="mb-0">Document Types</h6>
                            <div class="text-muted" style="font-size: 0.9em;">{{ (selectedCompItemIds.length ? `${selectedCompItemIds.length} of ` : '') + compItemFilterOptions.length }} Document Types</div>
                        </div>
                        <div class="px-3 pt-2 d-flex gap-4 align-items-center justify-content-start bg-white">
                            <b-link variant="bare-primary" class="font-size-70 text-decoration-none" @click="() => selectedCompItemIds = compItemFilterOptions.map(o => o.id)">Select All</b-link>
                            <b-link variant="bare-primary" class="font-size-70 text-decoration-none" @click="() => selectedCompItemIds = []">Reset</b-link>
                        </div>
                        <PrimeListbox 
                            v-model="selectedCompItemIds" 
                            :options="compItemFilterOptions" 
                            dataKey="id"
                            optionValue="id"
                            optionLabel="title" 
                            :multiple="true"
                            listStyle="max-height:400px"
                            :filter="compItemFilterOptions.length > 10"
                            filterPlaceholder="Document Type..."
                            placeholder="Select Document Types"
                        >
                            <template #option="slotProps">
                                <div class="d-flex align-items-center gap-2 font-size-90 line-height-100">
                                    <b-icon :icon="selectedCompItemIds.includes(slotProps.option.id) ? 'check-square-fill' : 'square'" variant="primary"></b-icon>
                                    <div>{{ slotProps.option.title }}</div>
                                </div>
                            </template>
                        </PrimeListbox>
                    </div>
                    <div v-if="enableStatusFilter" class="vci-list-filter-wrapper">
                        <div class="vci-list-filter-heading">
                            <h6 class="mb-0">Status</h6>
                            <div class="text-muted" style="font-size: 0.9em;">{{ (selectedStatusKeys.length ? `${selectedStatusKeys.length} of ` : '') + statusFilterOptions.length }} Status Options</div>
                        </div>
                        <div class="px-3 pt-2 d-flex gap-4 align-items-center justify-content-start bg-white">
                            <b-link variant="bare-primary" class="font-size-70 text-decoration-none" @click="() => selectedStatusKeys = statusFilterOptions.map(o => o.key)">Select All</b-link>
                            <b-link variant="bare-primary" class="font-size-70 text-decoration-none" @click="() => selectedStatusKeys = []">Reset</b-link>
                        </div>
                        <PrimeListbox 
                            v-model="selectedStatusKeys" 
                            :options="statusFilterOptions" 
                            dataKey="key"
                            optionValue="key"
                            optionLabel="label" 
                            :multiple="true"
                            listStyle="max-height:400px"
                            placeholder="Status..."
                        >
                            <template #option="slotProps">
                                <div class="d-flex align-items-center gap-2 font-size-90 line-height-100">
                                    <b-icon :icon="selectedStatusKeys.includes(slotProps.option.key) ? 'check-square-fill' : 'square'" variant="primary"></b-icon>
                                    <b-badge
                                        :id="`status-badge-${slotProps.option.key}`"
                                        :variant="slotProps.option.variant"
                                        class="text-uppercase"
                                        v-b-tooltip.hover.right.window
                                        :title="slotProps.option.description"
                                    >
                                        {{ slotProps.option.label }}
                                    </b-badge>
                                </div>
                            </template>
                        </PrimeListbox>
                    </div>
                    <div class="">
                        <b-button 
                            variant="minimal-primary" 
                            size="sm" 
                            class="button-text"
                            @click="refreshList"
                        >
                            <b-icon icon="arrow-repeat"></b-icon>
                            Refresh
                        </b-button>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md-8 col-lg-9">
                <div
                    v-if="unfilteredRowCount < 1"
                    key="noItems"
                    class="border bg-gray-100 rounded-lg d-flex align-items-center justify-content-center p-3 min-height-200 text-muted"
                >
                    <div class="text-center">
                        <div class="special-text mt-2">{{ loading ? 'Loading...' : 'No Documents' }}</div>
                    </div>
                </div>
                <b-card
                    v-else
                    key="itemList"
                    class="shadow-sm overflow-hidden rounded-lg"
                    no-body
                >
                    <b-table
                        key="theTable"
                        primary-key="id"
                        ref="vendorCompItemsTable"
                        :items="filteredItems"
                        :fields="tableFields"
                        :busy="loading"
                        :sort-by.sync="sortByField"
                        :sort-desc.sync="sortDesc"
                        :per-page="perPage"
                        :current-page="currentPage"
                        hover
                        responsive
                        selectable
                        select-mode="single"
                        selected-variant="primary"
                        :tbody-tr-class="getRowClass"
                        no-select-on-click
                        class="mb-0 bg-white no-top-border vendor-comp-item-status-table"
                    >
                        <!-- ROW DETAILS --> 
                        <template #row-details="row">
                            <div class="bg-gray-100">
                                {{ row.item.id }} <!-- just a placeholder for now -->
                            </div>
                        </template>
                        <!-- <template #cell(detailsToggleButton)="data">
                            <b-button
                                size="md"
                                variant="minimal-secondary"
                                class="p-1"
                                @click="data.toggleDetails"
                            >
                                <b-icon :icon="data.detailsShowing ? 'dash-circle' : 'plus-circle'"></b-icon>
                            </b-button>
                        </template> -->
                        <template #cell(detailsToggle)="data">
                            <b-link
                                class="text-secondary font-size-110"
                                @click="data.toggleDetails"
                            >  <!-- @click="data.toggleDetails" -->
                                <b-icon :icon="data.detailsShowing ? 'dash-circle' : 'plus-circle'"></b-icon>
                            </b-link>
                        </template>
                        <!-- END ROW DETAIL STUFF -->
                        <template #cell(title)="data">
                            <span>
                                <b-link 
                                    aria-controls="vendor-comp-item-detail-sidebar"
                                    :aria-expanded="detailStatusObjectId === data.item.id ? 'true' : 'false'"
                                    @click="setDetailItem(data.item, data.index, $event)">
                                    {{ data.value }}
                                </b-link>  <!-- aria-expanded should be STRING of true/false depending on whether this rows thing is in sidebar or not -->
                                <ActiveAssigneeChip
                                    v-if="vciIdsWithActiveTasks.includes(data.item.vendor_comp_item_id)" 
                                    :id="`active-tasks-badge-${data.item.id}`"
                                    variant="warning"
                                    text="You"
                                    tooltip="Waiting on your action"
                                    class="ml-2"
                                ></ActiveAssigneeChip>
                                <!-- <span 
                                    v-if="vciIdsWithActiveTasks.includes(data.item.vendor_comp_item_id)" 
                                    :id="`active-tasks-badge-${data.item.id}`"
                                    class="border rounded-pill bg-warning-100 font-weight-500 text-uppercase ml-2"
                                    style="font-size: 12px; padding: 2px 5px; cursor: default;"
                                    v-b-tooltip.hover
                                    title="Waiting on your action"
                                >
                                    <b-icon icon="circle-fill" animation="throb" scale="1" variant="warning-600" style="margin-right: 2px;"></b-icon>
                                    You
                                </span> -->
                            </span>
                        </template>

                        <template v-slot:cell(vendor)="data">
                            <b-link 
                                :href="data.unformatted.url" 
                                target="_blank" 
                                rel="noopener"
                            >
                                {{ data.unformatted.name }}
                            </b-link>
                        </template>

                        <template v-slot:cell(comp_item)="data">
                            <span class="">
                                <b-link 
                                    v-if="currentUserIsInternal"
                                    key="isInternal"
                                    :href="data.unformatted.urls.permalink" 
                                    target="_blank" 
                                    rel="noopener"
                                >
                                    {{ data.value }}
                                </b-link>
                                <span v-else key="notInternal">{{ data.value }}</span>
                            </span>
                        </template>

                        <template v-slot:cell(kind)="data">
                            <CompKindChip :kind="data.value"></CompKindChip>
                        </template>
                        <template v-slot:cell(overall_status)="data">
                            <div class="d-flex flex-column align-items-center gap-1">
                                <b-badge
                                    :id="`overall-status-badge-${data.item.id}`"
                                    :variant="data.unformatted.variant"
                                    class="text-uppercase"
                                    v-b-tooltip.hover.right.window
                                    :title="data.unformatted.description"
                                >
                                    {{ data.unformatted.label }}
                                </b-badge>
                                <!-- <ActiveAssigneeChip
                                    v-if="data.item.is_pending_internal_review"
                                    :id="`pending_internal-review-chip-${data.item.id}`"
                                    variant="secondary"
                                    text="New Version Pending"
                                    tooltip="New version submitted, pending internal review"
                                    font-size="10px"
                                ></ActiveAssigneeChip> -->
                                <span
                                    v-if="data.item.is_in_progress"
                                    :id="`in-progress-badge-${data.item.id}`"
                                    class="text-uppercase font-scale-70"
                                    v-b-tooltip.hover.right.window
                                    title="Submission in progress"
                                >
                                    <b-icon 
                                        icon="circle-fill" 
                                        animation="throb" 
                                        scale="1" 
                                        variant="primary-600" 
                                        :style="{marginRight: '2px'}"
                                    ></b-icon>
                                    In Progress
                                </span>
                            </div>
                        </template>
                        <template v-slot:cell(valid_to)="data">
                            <div class="d-flex align-items-center justify-content-center gap-3">
                                <div :class="data.item.is_expired ? 'text-danger' : ''">{{ formatDate((data.item.valid_to_date_tenant_tz), '-') }}</div>
                                <b-badge
                                    v-if="data.item.hasExpireActionSpecs && data.unformatted"
                                    variant="secondary"
                                    class="text-uppercase"
                                    v-b-tooltip.hover
                                    title="Reminder is set"
                                >R</b-badge>
                                <!-- color code this, put humanized relative time -->
                            </div>
                        </template>
                        <template v-slot:cell(files)="data">
                            <base-s-3-file-download-button-group
                                :fileData="data.value"
                                :button-props="{
                                    block: true,
                                    buttonVariant: 'subtle-primary',
                                    hideFilesize: true,
                                }"
                                vertical
                                class="w-100 max-width-300"
                            ></base-s-3-file-download-button-group>
                        </template>
                        <template v-slot:cell(actions)="data">
                            <b-button-group>
                                <b-button @click="setDetailItem(data.item, data.index, $event)" size="sm" variant="minimal-primary" class="special-text">Edit</b-button>
                            </b-button-group>
                        </template>
                    </b-table>
                    <template #footer v-if="!loading && filteredRowCount">
                        <base-table-pagination
                            :current-page.sync="currentPage"
                            :per-page.sync="perPage"
                            :per-page-options="perPageOptions"
                            :total-items="filteredRowCount"
                            :show-per-page="showPerPage"
                            item-name="Vendor Documents"
                        ></base-table-pagination>
                    </template>
                </b-card>
            </div>
        </div>

        <!-- DETAIL SIDEBAR v-model="sidebarShowing" -->
        <b-sidebar 
            v-model="sidebarShowing"
            id="vendor-comp-item-detail-sidebar"
            right 
            :width="complianceSettings.sidebarWidth" 
            shadow
            lazy
            :aria-label="detailStatusObjectTitle"
            bg-variant="gray-100"
            body-class="py-4 px-3"
            @shown="handleSidebarShown"
            @hidden="handleSidebarHidden"
            @change="handleSidebarChange"
        >
            <!-- <transition name="fade-fast" mode="out-in"> -->
            <!-- TODO - make this work for CompItem/Vendor combos where you don't have VendorCompItem yet 
            make sure to get the right IDs for the right things (keys/sending to API, etc.) 
            -->
            <VendorCompItemDetail
                v-if="sidebarShowing && detailVendorCompItemId"
                :key="detailStatusObjectId"
                :vendorCompItemId="detailVendorCompItemId"
                @updated="refreshList"
                @deleted="refreshList"
            ></VendorCompItemDetail>
            <!-- </transition> -->
        </b-sidebar>  

        <b-modal
            v-if="allowCreate"
            v-model="addModalShowing"
            centered
            title="Add Vendor Contract or Certificate"
            body-bg-variant="gray-100"
            hide-footer
        >
            <ScopedVendorCompItemForm
                :vendorId="vendorId"
                @cancel="handleCreateFormCancel"
                @saved="handleCreateFormSaved"
                @loading-status="handleCreateFormLoadingStatus"
            ></ScopedVendorCompItemForm>
        </b-modal>
    
    </div>
</template>

<script>
import { suppliosApi } from '@/js/api-service';
import { errorHandling, currentUserInfoMixin } from '@/js/custom-vue-mixins';
import ScopedVendorCompItemForm from './ScopedVendorCompItemForm.vue';
import { isFutureDate, FormatDateHelper } from '@/js/filters-helpers';
import { VendorCompItemStatus } from './shared/compModels';
import { ComplianceSettingsMixin } from './shared/complianceMixins';
import BaseTablePagination from '@/components/BaseTablePagination.vue';
import VendorCompItemDetail from './VendorCompItemDetail.vue';
import CompKindChip from './shared/CompKindChip.vue';
// import PrimeMultiSelect from 'primevue/multiselect';
import PrimeListbox from 'primevue/listbox';
import sortBy from 'lodash/sortBy';
import ActiveAssigneeChip from './shared/ActiveAssigneeChip.vue';

export default {
    name: 'VendorCompItemList',
    components: {
        ScopedVendorCompItemForm,
        BaseTablePagination,
        VendorCompItemDetail,
        CompKindChip,
        // PrimeMultiSelect,
        PrimeListbox,
        ActiveAssigneeChip,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
        ComplianceSettingsMixin,
    ],
    props: {
        vendorId: {  // if we only want to get for a SINGLE vendor (allow multiple vendors later?)
            type: String,
            required: false,
        },
        compItemId: {  // if we only want to get for a SINGLE comp item (allow multiple later?)
            type: String,
            required: false,
        },
        enabledColumns: { // KEYS of the columns to show, in order
            type: Array,
            default: () => [],
        },
        onlyExpiring: {
            type: Boolean,
            default: false,
        },
        onlyInProgress: {
            type: Boolean,
            default: false,
        },
        initialSortField: {  // initial field for sortBy, does not have to be in showFields array, but must be in fields array and must be in the ordering options for API endpoint
            type: String,
            default: 'valid_to',  // we sort by 'valid_to' but there is a sortByFormatted on that field to sort by sorting timestamp, which is what we want
        },
        initialSortDesc: {  // initial sort in descending order?
            type: Boolean,
            default: false,
        },
        heading: {
            type: String,
            default: '',
        },
        noCreate: {  // if true, then we do not show the create button, no matter what
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            debugShowing: false,
            loading: false,
            firstLoadComplete: false,
            loadingSubmissionStates: false,
            addModalShowing: false,
            items: [],
            activeTaskIdsByVciId: {},  // key is vci_id, value is array of active task (FlowNode) IDs for that VCI
            // submissionStates: [],
            allTableFields: [
                //{ key: 'detailsToggle', label: '', sortable: false, class: 'max-width-50 text-center' },
                { key: 'title', label: 'Title', class: 'font-weight-bold', sortable: true },
                { key: 'vendor', label: 'Vendor', class: 'text-left', sortable: true, formatter: (value) => {
                    return value.name;
                }, sortByFormatted: true },
                { key: 'comp_item', label: 'Document Type', class: 'text-center', sortable: true, formatter: (value) => {
                    return value?.title || '';
                }, sortByFormatted: true },
                { key: 'kind', label: 'Kind', class: 'text-center' },  // has { label, icon, and more... }
                { key: 'valid_to', label: 'Expires', class: 'text-center', sortable: true, formatter: (value, key, item) => { return item.valid_to_timestamp ;}, sortByFormatted: true },
                // show state/files only for primary, if primary is valid?
                { key: 'overall_status', label: 'Status', class: 'text-center', sortable: true, formatter: (value) => {
                    return value.label;
                }, sortByFormatted: true },
                { key: 'files', label: 'Files', class: ['max-width-300', 'width-300'], formatter: (value, key, item) => {
                    return item.primarySubmissionFiles;
                }, sortable: false },
                //{ key: 'actions', label: 'Actions', class: 'text-center' },
            ],
            // UPDATE FORM / SIDEBAR
            sidebarShowing: false,
            detailData: {}, // set the status object here to show in the sidebar
            // pagination
            showPerPage: true,
            perPage: 25,
            perPageOptions: [10, 25, 50, 100],
            currentPage: 1,
            sortByField: 'valid_to',  // default gets set via prop... sortByField and sortDesc will get .sync'd by b-table, then gets used in API call query params
            sortDesc: false, 
            // FILTERS - currently all local filters
            vendorFilterOptions: [], // set when we get items
            selectedVendorIds: [],
            compItemFilterOptions: [],  // set when we get items
            selectedCompItemIds: [],
            statusFilterOptions: [],  // set when we get items
            selectedStatusKeys: [],
            kindFilterOptions: [],  // set when we get items
            selectedKindIds: [],
        }
    },
    computed: {
        vciIdsWithActiveTasks() {
            // we shouldn't have an entry in the activeTaskIdsByVciId if there are no active tasks for that VCI
            return Object.keys(this.activeTaskIdsByVciId);
        },
        // FILTERS
        enableVendorFilter() {
            return this.visibleTableFieldKeys.includes('vendor') && this.vendorFilterOptions.length > 1;
        },
        enableCompItemFilter() {
            return this.visibleTableFieldKeys.includes('comp_item') && this.compItemFilterOptions.length > 1;
        },
        enableStatusFilter() {
            return this.visibleTableFieldKeys.includes('overall_status') && this.statusFilterOptions.length > 1;
        },
        enableKindFilter() {
            return this.visibleTableFieldKeys.includes('kind') && this.kindFilterOptions.length > 1;
        },
        filteredItems() {
            // THIS IS WHAT WE USE FOR THE TABLE ITEMS
            // we filter based on the selected filters
            return this.items.filter((item) => {
                const passesVendor = !this.enableVendorFilter || this.selectedVendorIds.length < 1 || this.selectedVendorIds.includes(item.vendor.id);
                const passesCompItem = !this.enableCompItemFilter || this.selectedCompItemIds.length < 1 || this.selectedCompItemIds.includes(item.comp_item.id);
                const passesStatus = !this.enableStatusFilter || this.selectedStatusKeys.length < 1 || this.selectedStatusKeys.includes(item.overall_status.key);
                const passesKind = !this.enableKindFilter || this.selectedKindIds.length < 1 || this.selectedKindIds.includes(item.kind.id);  // or just item.kind_id
                return passesVendor && passesCompItem && passesStatus && passesKind;
            });
        },
        unfilteredRowCount() {
            return this.items.length;
        },
        filteredRowCount() {
            return this.filteredItems.length;
        },
        allowCreate() {
            return !this.noCreate && !!this.vendorId;  // if NOT vendor specific, then we don't allow creation -- but we can change this later if we update create form
        },
        createButtonEnabled() {
            // TODO - change this later based on permissions in flow transitions?  should be dynamic?  or just let the options loaded be dynamic?
            // TODO - change based on whether the current TU can add any... do a fetch when we first load the list?
            return this.allowCreate && this.currentUserIsInternal && !this.sidebarShowing;
        },
        // TODO - ideally, should group by vendor when showing mixed vendors, especially if we allow multiple per vendor
        tableFields() {
            const hideIf = {
                'vendor': !!this.vendorId,  // if we specify vendorId, then don't show vendor column
                'comp_item': !!this.compItemId,  // if we specify compItemId, then don't show comp_item column
                'itemTypeName': !!this.compItemId,  // if we specify compItemId, then don't show itemTypeName column (because they will all be the same)
                'kind': !!this.compItemId,  // same as above
            }
            const finalFields = [];
            if (Array.isArray(this.enabledColumns) && this.enabledColumns.length > 0) {
                // if we have enabledColumns set, we only show those columns, in that order
                // but we must still check the hideIf rules, and make sure they are in allTableFields
                this.enabledColumns.forEach((colKey) => {
                    if (this.allTableFields.some(f => f.key === colKey) && !hideIf[colKey]) {
                        finalFields.push(this.allTableFields.find(f => f.key === colKey));
                    }
                });
            } else {
                this.allTableFields.forEach((field) => {
                    if (!hideIf[field.key]) {
                        finalFields.push(field);
                    }
                });
            }
            return finalFields;
        },
        visibleTableFieldKeys() {
            return this.tableFields.map(f => f.key);
        },
        detailStatusObjectId() {
            return this.detailData?.id || '';
        },
        detailStatusObjectTitle() {
            return this.detailData?.title || '';
        },
        detailVendorCompItemId() {
            // this MAY be the same as ID of the detailData status object, but only if the VCI exists (not just a CompItem/Vendor combo)
            return this.detailData?.vendor_comp_item_id || '';
        },
        currentUserIsInternal() {
            return !!this.$currentUserInfo?.is_internal;
        },
        currentTuIsFullUser() {
            return !!this.$currentUserInfo?.is_paid_seat;
        },
        anyFiltersEnabled() {
            return this.enableVendorFilter || this.enableCompItemFilter || this.enableStatusFilter;
        },
        showFilterHr() {
            return this.allowCreate;  // we only check if create button is there... will separate the refresh button if no filters, so still needed
            //   && this.anyFiltersEnabled;
        },
    },
    created() {
        this.setInitialFromDefaults();
    },
    mounted() {
        this.resetCreateForm();
        // this.getMeta();  // useful for side-loading things, but not currently used, we have all this in the list now
        this.getItems();
    },
    methods: {
        async refreshList() {
            return await this.getItems();
        },
        async getItems() {
            this.loading = true;
            let resp;
            try {
                // NEW - the new generic API endpoint
                // must set some things based on what cols you want to show also
                resp = await suppliosApi.compItemStd.list_vendor_comp_items({
                    vendor_ids: this.vendorId ? [this.vendorId] : [],  // if we have a vendorId, we only want to show for that vendor
                    comp_item_ids: this.compItemId ? [this.compItemId] : [],  // if we have a compItemId, we only want to show for that comp item
                    with_submissions: true,  // must include this if you want to show the 'files/primarySubmissionFiles' column
                    only_expiring: this.onlyExpiring,
                    only_in_progress: this.onlyInProgress,
                    include_empty: false,  // set true if you want to show status objects for all CompItem/Vendor combos, even if no VCI exists
                    with_eas: false,  // not needed in list view, only detail view
                    with_tasks: true,  // include ACTIVE tasks for the VCI/CompSubs for the current TU
                });
                // console.log('resp from list_vendor_comp_items', resp);
                // this.items = Array.isArray(resp.data.status_list) ? resp.data.status_list.map((apiData) => {
                //     return new VendorCompItemStatus(apiData);
                // }) : [];
                this.items = resp.data.status_list.map((apiData) => new VendorCompItemStatus(apiData));  // this class currently does not do anything with 'tasks' if passed in... so not needed for this
                if (resp.data.tasks) {
                    // we get the tasks IDs for the VCI (ones assigned to current TU), so we can show 'You' badge on rows that have active tasks for current TU'
                    // this is an object with keys as vci_id and values as array of task (FlowNode) IDs -- no longer full objects
                    // UPDATE Aug 30 2025 -- we don't need full tasks objects here, that can be expensive and we don't need it
                    // just return IDs of the FlowNodes (tasks) instead of full objects... same thing here basically
                    this.activeTaskIdsByVciId = resp.data.tasks;
                }
                this.setFilterOptions(this.items);
            } catch (error) {
                this.apiErrorHandler(error);                
            }
            this.loading = false;
            this.firstLoadComplete = true;
        },
        setInitialFromDefaults() {
            this.sortByField = this.initialSortField;  // set initial sort field based on prop
            this.sortDesc = this.initialSortDesc;  // set initial sort direction based on prop
        },
        // SET FILTERS FROM ITEMS
        setFilterOptions(items) {
            // lumped all filter gathering together... but we could do this separately if we want
            const vendorsById = {};
            const compItemsById = {};
            const statusByKey = {};
            const kindById = {};
            items.forEach((item) => {
                if (!vendorsById[item.vendor.id]) {
                    vendorsById[item.vendor.id] = item.vendor;
                }
                const compItem = item.comp_item; // might not have it if we didn't get from API
                if (compItem && !compItemsById[compItem.id]) {
                    compItemsById[compItem.id] = compItem;
                }
                if (!statusByKey[item.overall_status.key]) {
                    statusByKey[item.overall_status.key] = item.overall_status;
                }
                if (!kindById[item.kind.id]) {
                    kindById[item.kind.id] = item.kind;
                }
            });
            this.vendorFilterOptions = sortBy(Object.values(vendorsById), [function(o) { return o.name.toUpperCase(); }]);
            this.compItemFilterOptions = sortBy(Object.values(compItemsById), [function(o) { return o.title.toUpperCase(); }]);
            this.statusFilterOptions = sortBy(Object.values(statusByKey), [function(o) { return o.label.toUpperCase(); }]);
            this.kindFilterOptions = sortBy(Object.values(kindById), [function(o) {return o.label.toUpperCase(); }]);
            
            // we also clear all the selected filters when we get new items
            this.selectedVendorIds = [];
            this.selectedCompItemIds = [];
            this.selectedStatusKeys = [];
            this.selectedKindIds = [];
        },
        getRowClass(item, type) {
            // item is the row data, type is 'row' or 'cell'
            // we can use this to set the row class based on the item data
            const classes = [];
            if (type === 'row') {
                // we can set the row class based on the item data - will be our VCI status objects
                // console.log('getRowClass item', item);
                // we will check if there are any active tasks for this VCI, and if so, we will set the row class to 'has-active-tasks'
                if (this.vciIdsWithActiveTasks.includes(item.vendor_comp_item_id)) {
                    classes.push('has-active-tasks');  // this will add a class to the row if there are active tasks for this VCI
                }
                // we can also check if the item is expired, and set a class for that??
            }
            return classes;  // return a string or an object with classes to apply to the row
        },
        // DETAILS / UPDATES
        selectTableRow(displayedRowIndex) {
            // pass in the index of the row in the table you want to show in selected state
            // hard to pass in the ID, since the index of each row changes based on filtering/sorting of the table
            // if you pass in a falsy value (excluding 0), it will clear the selected state
            // the table needs an index of the row... we'll look that up
            if (displayedRowIndex !== null && displayedRowIndex !== undefined) {
                if (displayedRowIndex > -1) {
                    this.$refs.vendorCompItemsTable?.selectRow(displayedRowIndex);
                }
            } else {
                this.$refs.vendorCompItemsTable?.clearSelected();
            }
        },
        setDetailItem(vciStatus, displayedRowIndex, event) {
            console.log('setDetailItem vciStatus, displayedRowIndex, $event', vciStatus, displayedRowIndex, event);
            this.detailData = vciStatus;
            this.sidebarShowing = true;
            //this.addModalShowing = true;
            // select the row in the table to show in selected state
            this.selectTableRow(displayedRowIndex);
        },
        showAddModal() {
            this.addModalShowing = true;
        },
        resetCreateForm() {
            this.addModalShowing = false;
            this.sidebarShowing = false;
            this.detailData = {};
            // clear the selected row in the table to NOT show in selected state
            this.selectTableRow(null);
        },
        handleCreateFormLoadingStatus(status) {
            console.log(status);
        },
        handleCreateFormSaved() {
            this.getItems();
            this.resetCreateForm();
        },
        handleCreateFormCancel() {
            this.resetCreateForm();
        },
        toggleDebug() {
            this.debugShowing = !this.debugShowing;
        },
        isFutureDate(dateString) {
            if (!dateString) {
                return true;  // we return true here so things say 'Expires' if not set
            }
            return isFutureDate(dateString);
        },
        formatDate(dt, defaultText) {
            return FormatDateHelper(dt, defaultText);
        },
        handleSidebarHidden() {
            this.selectTableRow(null);  // unselect the row in the table
        },
        handleSidebarShown() {
            // do something when the sidebar is shown
            console.log('handleSidebarShown');
        },
        handleSidebarChange(newVal) {
            // handles @change event from b-sidebar, which is the v-model for sidebarShowing
            // ideally, we should not set v-model directly, that screws up assistive tech stuff
            // instead, we use v-b-toggle directive and update other things based on this emitted event
            // but we counldn't get that to work with the sidebar, so we're doing this
            console.log('handleSidebarChange', newVal);
            // this.sidebarShowing = newVal;
        },
    },
}
</script>

<style lang="scss">
.vendor-comp-item-list {
    .vci-list-filter-wrapper {
        border: 1px solid var(--gray-300);
        overflow: hidden;
        box-shadow: 0 0 5px rgba(0,0,0,0.05);
        border-radius: 0.5rem;
        .vci-list-filter-heading {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 0;
            background-color: var(--primary-100);  // rgba(0,0,0,0.05);
            padding: 0.5rem 1rem;
        }
        .p-listbox {
            border-color: transparent;
        }
    }

    .vendor-comp-item-status-table {
        tr.has-active-tasks {
            // background-color: var(--success-100) !important;  // background for rows with active tasks
            &> td:first-child {
                //border-left: 3px solid var(--primary-500) !important;  // border (in first col) for rows with active tasks
            }
        }
    }

}
</style>